// Copyright (c) Microsoft. All rights reserved.

import {
    Badge,
    Divider,
    InteractionTag,
    InteractionTagPrimary,
    makeStyles,
    shorthands,
    TagGroup,
    tokens,
} from '@fluentui/react-components';
import { DocumentPdf20Regular, DocumentTextLink20Regular } from '@fluentui/react-icons';
import React from 'react';
import { Citation, IChatMessage } from '../../../libs/models/ChatMessage';
import { customTokens } from '../../../styles';
import { useFile } from '../../../libs/hooks';

interface ICitationTagsProps {
    message: IChatMessage;
}

const useClasses = makeStyles({
    root: {
        marginTop: '-10px',
        flexWrap: 'wrap',
        ...shorthands.gap(customTokens.spacingHorizontalS),
    },
    button: {
        backgroundColor: tokens.colorNeutralBackground4,
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground6,
        },
    },
});

export const CitationTags: React.FC<ICitationTagsProps> = ({ message }) => {
    const classes = useClasses();
    const file = useFile();

    if (!message.citations || message.citations.length === 0) {
        return null;
    }

    const openDocument = (citation: Citation) => {
        const documentId = citation.link.split('/')[0];

        if (!documentId) {
            return;
        }

        file.openDocument(documentId, citation.sourceName).catch(console.log);
    };

    const showIndex = message.citations.length > 1;

    return (
        <>
            <Divider alignContent="start">Referenzen</Divider>
            <TagGroup className={classes.root}>
                {message.citations.map((citation, index) => {
                    const icon = citation.sourceName.endsWith('.pdf') ? (
                        <DocumentPdf20Regular />
                    ) : (
                        <DocumentTextLink20Regular />
                    );

                    return (
                        <InteractionTag
                            key={`citation-card-${index}`}
                            shape="circular"
                            size="small"
                            onClick={() => {
                                openDocument(citation);
                            }}
                        >
                            <InteractionTagPrimary className={classes.button} icon={icon}>
                                {citation.sourceName}
                                {showIndex && (
                                    <Badge appearance="ghost" size="small">
                                        {index + 1}
                                    </Badge>
                                )}
                            </InteractionTagPrimary>
                        </InteractionTag>
                    );
                })}
            </TagGroup>
        </>
    );
};
